import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import followUpSelector from '../../../state/ducks/followUps/selectors';
import { userSelectors } from '../../../state/ducks/user/selectors';
import { userDetails } from '../../../state/ducks/users/users';
import { RootState } from '../../../state/store/store';
import { IFollowUpExtended } from './FollowUp';

// Removed object dependency
type Props = {
    followUps: IFollowUpExtended[];
    users: userDetails[];
    currentUserId: string;
};

// Create our own type that includes all the properties we need
type FollowUpTask = IFollowUpExtended & {
    shipmentId: string;
    // Add other properties we need access to
    priority?: 'high' | 'medium' | 'low' | string;
};

// Helper function to determine if a date is passed
const isDatePassed = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
};

// Helper function to determine if a date is nearing (within 3 days)
const isDateNearing = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);

    // Date is not passed but within 3 days
    if (compareDate >= today) {
        const diffTime = Math.abs(compareDate.getTime() - today.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays <= 3;
    }
    return false;
};

export const MyFollowUps = ({ followUps, users, currentUserId }: Props) => {
    // Filter follow-ups to only show those assigned to the current user
    const [myFollowUps, setMyFollowUps] = useState<FollowUpTask[]>([]);
    const [summaryStats, setSummaryStats] = useState({
        pastDue: 0,
        nearingDue: 0,
        upcomingTasks: 0,
        completedTasks: 0,
        highPriority: 0
    });

    useEffect(() => {
        // Find the current user's name from the users list
        const currentUser = users.find((user) => user.id === currentUserId);
        const currentUserFullName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : '';

        // Filter follow-ups to only show those assigned to the current user
        const userFollowUps = followUps.filter(
            (followUp) =>
                // Check if the assignedTo field contains the current user ID or full name
                followUp.assignedTo === currentUserId || followUp.assignedTo === currentUserFullName
        ) as FollowUpTask[];

        setMyFollowUps(userFollowUps);

        // Calculate summary statistics
        const stats = {
            pastDue: 0,
            nearingDue: 0,
            upcomingTasks: 0,
            completedTasks: 0,
            highPriority: 0
        };

        // Create a Set to track unique shipment IDs for each category
        const pastDueShipments = new Set<string>();
        const nearingDueShipments = new Set<string>();

        userFollowUps.forEach((followUp) => {
            // Track completed tasks
            if (followUp.isCompleted) {
                stats.completedTasks++;
                return;
            }

            // Track high priority tasks
            // Safely check for priority - it might not exist on all follow-ups
            if (followUp.priority && followUp.priority === 'high') {
                stats.highPriority++;
            }

            // Track due date statuses and count unique shipments
            if (followUp.dueDate) {
                if (isDatePassed(followUp.dueDate)) {
                    pastDueShipments.add(followUp.shipmentId);
                } else if (isDateNearing(followUp.dueDate)) {
                    nearingDueShipments.add(followUp.shipmentId);
                } else {
                    stats.upcomingTasks++;
                }
            } else {
                // Tasks without due dates count as upcoming
                stats.upcomingTasks++;
            }
        });

        // Update stats with unique shipment counts
        stats.pastDue = pastDueShipments.size;
        stats.nearingDue = nearingDueShipments.size;

        setSummaryStats(stats);
    }, [followUps, users, currentUserId]);

    return (
        <PageWrapper>
            <ContentWrapper>
                {myFollowUps.length > 0 ? (
                    <SummaryContainer>
                        <SummaryCard $status="overdue">
                            <SummaryCount>{summaryStats.pastDue}</SummaryCount>
                            <SummaryText>Shipments are past their due date</SummaryText>
                        </SummaryCard>

                        <SummaryCard $status="nearing">
                            <SummaryCount>{summaryStats.nearingDue}</SummaryCount>
                            <SummaryText>Shipments are nearing their due date</SummaryText>
                        </SummaryCard>
                    </SummaryContainer>
                ) : (
                    <EmptyState>You don't have any assigned follow-up tasks.</EmptyState>
                )}
            </ContentWrapper>
        </PageWrapper>
    );
};

const PageWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 20px;
    gap: 20px;
`;

const ContentWrapper = styled.div`
    flex: 1;
    overflow: auto;
    width: 100%;
`;

const EmptyState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
    font-size: 16px;
    color: #999;
    background-color: #f9f9f9;
    border-radius: 8px;
`;

const SummaryContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr);
    gap: 16px;
    width: 100%;
`;

// Using $status with $ prefix for props to follow styled-components' transient props pattern
// This prevents the prop from being passed to the DOM element
const SummaryCard = styled.div<{ $status: 'overdue' | 'nearing' | 'priority' | 'normal' | 'completed' }>`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 24px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    background-color: ${(props) => {
        switch (props.$status) {
            case 'overdue':
                return '#fdeeee'; // Light red
            case 'nearing':
                return '#fff5e6'; // Light orange

            default:
                return '#ffffff';
        }
    }};
    border-left: 4px solid
        ${(props) => {
            switch (props.$status) {
                case 'overdue':
                    return '#dc3545'; // Red
                case 'nearing':
                    return '#fd7e14'; // Orange
                default:
                    return '#cccccc';
            }
        }};
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-2px);
    }
`;

const SummaryCount = styled.div`
    font-size: 36px;
    font-weight: 700;
    margin-bottom: 8px;
`;

const SummaryText = styled.div`
    font-size: 16px;
    text-align: center;
`;